const SHELF_SELECTOR = '[data-gs-merchandising-shelf]';
const CLAIM_SELECTOR = '[data-gs-merchandising-claim]';
const CARD_SELECTOR = '[data-gs-merchandising-card]';

if (!window.__gsMerchandisingCoordinatorLoaded) {
  window.__gsMerchandisingCoordinatorLoaded = true;

  const numericValue = (value, fallback) => {
    const parsed = Number.parseInt(value || '', 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const productHandle = (element) => {
    if (!(element instanceof HTMLElement)) return '';
    return (
      element.dataset.gsProductHandle ||
      element.querySelector('[data-gs-product-handle]')?.dataset.gsProductHandle ||
      ''
    )
      .trim()
      .toLowerCase();
  };

  const documentOrder = (left, right) => {
    if (left === right) return 0;
    const position = left.compareDocumentPosition(right);
    return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  };

  const setTrackedPosition = (card, position) => {
    const nextPosition = String(position);
    card.dataset.gsPosition = nextPosition;
    card.querySelectorAll('[data-gs-position]').forEach((element) => {
      if (element instanceof HTMLElement) element.dataset.gsPosition = nextPosition;
    });
  };

  const randomizedGridsReady = (shelves) =>
    shelves.every((shelf) => {
      const grid = shelf.querySelector('[data-gs-merchandising-grid]');
      if (!(grid instanceof HTMLElement)) return true;
      return grid.dataset.gsRandomizeProducts !== 'true' || grid.dataset.gsRandomized === 'true';
    });

  const coordinateScope = (scope, participants) => {
    const shelves = participants.filter((participant) => participant.matches(SHELF_SELECTOR));
    if (!randomizedGridsReady(shelves)) return;

    const claimedHandles = new Set();
    const sortedParticipants = participants.sort((left, right) => {
      const priorityDifference =
        numericValue(left.dataset.gsDedupePriority, 100) - numericValue(right.dataset.gsDedupePriority, 100);
      return priorityDifference || documentOrder(left, right);
    });

    sortedParticipants.forEach((participant) => {
      if (participant.matches(CLAIM_SELECTOR)) {
        const handle = productHandle(participant);
        if (handle) claimedHandles.add(handle);
        return;
      }

      const visibleLimit = Math.max(1, numericValue(participant.dataset.gsVisibleProductLimit, 4));
      const cards = Array.from(participant.querySelectorAll(CARD_SELECTOR));
      let visibleCount = 0;

      cards.forEach((card) => {
        if (!(card instanceof HTMLElement)) return;

        const handle = productHandle(card);
        const canShow = visibleCount < visibleLimit && (!handle || !claimedHandles.has(handle));
        card.hidden = !canShow;

        if (!canShow) return;

        visibleCount += 1;
        setTrackedPosition(card, visibleCount);
        if (handle) claimedHandles.add(handle);
      });

      participant.dataset.gsVisibleProductCount = String(visibleCount);
      participant.dataset.gsMerchandisingReady = 'true';
      participant.dispatchEvent(
        new CustomEvent('gs:merchandising:ready', {
          bubbles: true,
          detail: { scope, visibleCount },
        })
      );
    });
  };

  const coordinateMerchandising = (root = document) => {
    const queryRoot = root instanceof Element || root instanceof Document ? root : document;
    const participants = Array.from(queryRoot.querySelectorAll(`${SHELF_SELECTOR}, ${CLAIM_SELECTOR}`));

    if (queryRoot instanceof Element && queryRoot.matches(`${SHELF_SELECTOR}, ${CLAIM_SELECTOR}`)) {
      participants.unshift(queryRoot);
    }

    const scopes = new Map();
    participants.forEach((participant) => {
      if (!(participant instanceof HTMLElement)) return;
      const scope = (participant.dataset.gsDedupeScope || 'page').trim();
      if (!scopes.has(scope)) scopes.set(scope, []);
      scopes.get(scope).push(participant);
    });

    scopes.forEach((scopeParticipants, scope) => coordinateScope(scope, scopeParticipants));
  };

  let queued = false;
  const queueCoordination = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      coordinateMerchandising();
    });
  };

  window.gsMerchandising = {
    coordinate: coordinateMerchandising,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => coordinateMerchandising(), { once: true });
  } else {
    coordinateMerchandising();
  }

  document.addEventListener('gs:recommendation-rows-ready', queueCoordination);
  document.addEventListener('shopify:section:load', queueCoordination);
}
